/* Atelier Extérieur theme additions (Pre-Season AW26)
   1. Size must be chosen before adding to cart (PDP, quick add, sticky bar)
   2. Complete-the-look list (size select + add to cart)
   3. Recently viewed row with inline quick add
   4. Size help (fit line + size guide) inside the quick-add pop-up */

const SIZE_RE = /^\s*size\b/i;
const NEED = 'aeNeedSize';

/* ---------- cart helper: add and refresh the theme cart drawer/bubble ---------- */
let eventsModule = null;
async function loadEvents() {
  if (eventsModule !== null) return eventsModule;
  try { eventsModule = await import('@shopify/events'); } catch (e) { eventsModule = false; }
  return eventsModule;
}

export async function aeAddToCart(variantId, sourceEl, quantity = 1) {
  const ev = await loadEvents();
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId).filter(Boolean);
  let deferred = null;
  if (ev && ev.CartLinesUpdateEvent) {
    deferred = ev.CartLinesUpdateEvent.createPromise();
    (sourceEl || document).dispatchEvent(new ev.CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity }], promise: deferred.promise,
    }));
  }
  const fd = new FormData();
  fd.append('id', variantId);
  fd.append('quantity', quantity);
  if (sectionIds.length) fd.append('sections', sectionIds.join(','));
  const root = (window.Theme && Theme.routes) ? Theme.routes : { cart_add_url: '/cart/add', cart_url: '/cart' };
  const res = await fetch(root.cart_add_url, {
    method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
  }).then((r) => r.json());
  const cart = await fetch(`${root.cart_url}.js`).then((r) => r.json());
  if (deferred) {
    deferred.resolve({
      cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'product-form-component', sourceId: 'ae-custom', itemCount: quantity,
                productId: sourceEl?.dataset?.productId, sections: res.sections, didError: Boolean(res.status) },
    });
  }
  if (res.status) throw new Error(res.description || res.message || 'Could not add to cart');
  return cart;
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
  if (drawer && typeof drawer.open === 'function') drawer.open();
}

/* ---------- 1. Size must be chosen ---------- */
function sizeFieldset(vp) {
  return [...vp.querySelectorAll('fieldset')].find((fs) => {
    const legend = fs.querySelector('legend');
    return legend && SIZE_RE.test(legend.textContent) && fs.querySelectorAll('input[type=radio]').length > 1;
  });
}
function rootFor(vp) {
  return vp.closest('#quick-add-modal-content') || vp.closest('.product-details') || vp.closest('product-component') || vp.parentElement;
}
function clearSize(vp) {
  const fs = sizeFieldset(vp);
  if (!fs) return false;
  fs.querySelectorAll('input[type=radio]').forEach((i) => { i.checked = false; i.dataset.currentChecked = 'false'; });
  fs.classList.add('ae-size-fieldset');
  return true;
}
function initPicker(vp) {
  if (vp.closest('sticky-add-to-cart') || vp.closest('product-card')) return;
  const root = rootFor(vp);
  const pid = vp.dataset.productId || '';
  if (root && root.dataset.aeSizeFor && root.dataset.aeSizeFor !== pid) {
    delete root.dataset.aeSizeInit; sizeChosen(root);
  }
  if (!root || root.dataset.aeSizeInit === '1') {
    if (root && root.dataset[NEED] === '1') clearSize(vp);
    return;
  }
  const isMainPdp = !vp.closest('#quick-add-modal-content');
  if (isMainPdp && new URLSearchParams(location.search).has('variant')) return;
  if (!clearSize(vp)) return;
  root.dataset.aeSizeInit = '1';
  root.dataset.aeSizeFor = pid;
  root.dataset[NEED] = '1';
  root.classList.add('ae-need-size');
  if (isMainPdp) document.documentElement.classList.add('ae-pdp-need-size');
}
function sizeChosen(root) {
  delete root.dataset[NEED];
  root.classList.remove('ae-need-size');
  root.querySelectorAll('.ae-size-msg').forEach((m) => m.remove());
  if (!root.closest('#quick-add-modal-content')) document.documentElement.classList.remove('ae-pdp-need-size');
}
function flashSize(root) {
  const vp = root.querySelector('variant-picker');
  const fs = vp && sizeFieldset(vp);
  if (!fs) return;
  const next = fs.nextElementSibling;
  if (!(next && next.classList.contains('ae-size-msg'))) {
    const m = document.createElement('p');
    m.className = 'ae-size-msg';
    m.setAttribute('role', 'alert');
    m.textContent = 'Please choose a size';
    fs.after(m);
  }
  fs.classList.remove('ae-flash'); void fs.offsetWidth; fs.classList.add('ae-flash');
  if (!root.closest('dialog')) fs.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
document.addEventListener('change', (e) => {
  const input = e.target;
  if (!(input instanceof HTMLInputElement) || !e.isTrusted) return;
  if (!SIZE_RE.test(input.dataset.optionName || '')) return;
  const vp = input.closest('variant-picker');
  const root = vp && rootFor(vp);
  if (root && root.dataset[NEED] === '1') sizeChosen(root);
}, true);
document.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  // Main add to cart / accelerated checkout inside a root that still needs a size
  const root = t.closest('.ae-need-size');
  if (root && (t.closest('add-to-cart-component') || t.closest('.shopify-payment-button') || t.closest('.accelerated-checkout-block'))) {
    e.preventDefault(); e.stopImmediatePropagation(); flashSize(root); return;
  }
  // Sticky bar
  if (t.closest('sticky-add-to-cart button') && document.documentElement.classList.contains('ae-pdp-need-size')) {
    e.preventDefault(); e.stopImmediatePropagation();
    const mainRoot = document.querySelector('.product-details.ae-need-size');
    if (mainRoot) flashSize(mainRoot);
  }
}, true);
document.addEventListener('submit', (e) => {
  const root = e.target instanceof Element && e.target.closest('.ae-need-size');
  if (root) { e.preventDefault(); e.stopImmediatePropagation(); flashSize(root); }
}, true);

/* ---------- 4. Size help inside quick add ---------- */
const fitCache = {};
async function enhanceQuickAdd(modal) {
  const content = modal.querySelector('#quick-add-modal-content');
  if (!content) return;
  const vp = content.querySelector('variant-picker');
  const fs = vp && sizeFieldset(vp);
  if (!fs) return;
  const url = vp.dataset.productUrl;
  const existing = content.querySelector('.ae-qa-help');
  if (existing && existing.dataset.for === url) return;
  existing?.remove();
  if (!url || content.dataset.aeHelpLoading === url) return;
  content.dataset.aeHelpLoading = url;
  if (!(url in fitCache)) {
    try {
      const html = await fetch(`${url}?view=ae-fit`).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      fitCache[url] = { fit: doc.querySelector('[data-ae-fit]')?.textContent.trim() || '',
                        guide: doc.querySelector('[data-ae-size-guide]')?.getAttribute('data-ae-size-guide') || '' };
    } catch (e) { fitCache[url] = { fit: '', guide: '' }; }
  }
  delete content.dataset.aeHelpLoading;
  const { fit, guide } = fitCache[url];
  const fs2 = sizeFieldset(content.querySelector('variant-picker'));
  if (!fs2 || content.querySelector('.ae-qa-help')) return;
  const help = document.createElement('div');
  help.className = 'ae-qa-help';
  help.dataset.for = url;
  help.innerHTML = `${fit ? `<span class="ae-qa-help__fit">${fit}</span>` : ''}
    <span class="ae-qa-help__links">${guide ? `<a href="#" data-ae-guide="${guide}">Size guide</a>` : ''}
    <a href="${url}#ae-fit">Find your size</a></span>`;
  const msg = fs2.nextElementSibling?.classList.contains('ae-size-msg') ? fs2.nextElementSibling : null;
  (msg || fs2).after(help);
}
document.addEventListener('click', async (e) => {
  const a = e.target instanceof Element && e.target.closest('[data-ae-guide]');
  if (!a) return;
  e.preventDefault();
  const handle = a.getAttribute('data-ae-guide');
  let dlg = document.getElementById('ae-guide-dialog');
  if (!dlg) {
    dlg = document.createElement('dialog');
    dlg.id = 'ae-guide-dialog';
    dlg.className = 'ae-guide';
    dlg.innerHTML = '<button type="button" class="ae-guide__close" aria-label="Close">×</button><div class="ae-guide__body rte"></div>';
    dlg.querySelector('.ae-guide__close').addEventListener('click', () => dlg.close());
    dlg.addEventListener('click', (ev) => { if (ev.target === dlg) dlg.close(); });
    document.body.appendChild(dlg);
  }
  const body = dlg.querySelector('.ae-guide__body');
  body.innerHTML = 'Loading…';
  dlg.showModal();
  try {
    const html = await fetch(`/pages/${handle}`).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const rte = doc.querySelector('main .rte') || doc.querySelector('main');
    body.innerHTML = `<h3>Size guide</h3>${rte ? rte.innerHTML : ''}`;
  } catch (err) { body.textContent = 'Size guide unavailable.'; }
});

/* ---------- 2. Complete the look ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target instanceof Element && e.target.closest('.ae-ctl__add');
  if (!btn) return;
  e.preventDefault();
  const row = btn.closest('.ae-ctl__row');
  const select = row.querySelector('.ae-ctl__size');
  const id = select ? select.value : row.dataset.variantId;
  if (!id) { row.classList.remove('ae-flash'); void row.offsetWidth; row.classList.add('ae-flash'); select?.focus(); return; }
  btn.disabled = true; const label = btn.textContent; btn.textContent = 'Adding…';
  try { await aeAddToCart(id, row); btn.textContent = 'Added'; openCartDrawer(); }
  catch (err) { btn.textContent = 'Try again'; }
  setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 2200);
});

/* ---------- 3. Recently viewed ---------- */
const BAG_ICON = '<svg class="ae-rv__icon-bag" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width, 1.5)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/><path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width, 1.5)" d="M14.608 12.563v5m2.5-2.5h-5"/></svg>';
const CLOSE_ICON = '<svg class="ae-rv__icon-close" aria-hidden="true" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width, 1.5)" d="M5 5l10 10M15 5L5 15"/></svg>';
const RV_KEY = 'ae_recently_viewed';
function rvGet() { try { return JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (e) { return []; } }
function rvSet(list) { try { localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12))); } catch (e) {} }
const money = (cents) => {
  try {
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(cents / 100);
  } catch (e) { return (cents / 100).toFixed(2); }
};
async function renderRecentlyViewed(el) {
  const current = el.dataset.current;
  const list = rvGet().filter((h) => h !== current).slice(0, 8);
  if (current) rvSet([current, ...rvGet().filter((h) => h !== current)]);
  if (!list.length) return;
  const track = el.querySelector('.ae-rv__track');
  const products = (await Promise.all(list.map((h) =>
    fetch(`/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)))).filter((p) => p && p.available);
  if (!products.length) return;
  track.innerHTML = products.map((p) => {
    const img = (p.media && p.media[0] && p.media[0].src) || p.featured_image || '';
    const img2 = (p.media && p.media[1] && p.media[1].src) || '';
    const sizes = p.variants.filter((v) => v.available)
      .map((v) => `<button type="button" class="ae-rv__size" data-id="${v.id}">${p.options.length > 1 && p.options[0].values.length > 1 ? v.title : (v.options[v.options.length - 1] || v.title)}</button>`).join('');
    const src = (u, w) => (u ? `${u}${u.includes('?') ? '&' : '?'}width=${w}` : '');
    return `<div class="ae-rv__card" data-product-id="${p.id}">
      <div class="ae-rv__wrap"><a class="ae-rv__media" href="${p.url}">
        <img src="${src(img, 600)}" alt="${p.title.replace(/"/g, '&quot;')}" loading="lazy">
        ${img2 ? `<img class="ae-rv__alt" src="${src(img2, 600)}" alt="" loading="lazy">` : ''}
      </a>
      <button type="button" class="ae-rv__plus" aria-label="Quick add ${p.title.replace(/"/g, '&quot;')}">${BAG_ICON}${CLOSE_ICON}</button>
      <div class="ae-rv__sizes" hidden>${sizes}</div></div>
      <div class="ae-rv__info"><a href="${p.url}">${p.title}</a><span>${money(p.price)}</span></div>
    </div>`;
  }).join('');
  el.hidden = false;
}
document.addEventListener('click', async (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const plus = t.closest('.ae-rv__plus');
  if (plus) {
    const card = plus.closest('.ae-rv__card');
    const s = card.querySelector('.ae-rv__sizes');
    s.hidden = !s.hidden; card.classList.toggle('is-open', !s.hidden);
    return;
  }
  const size = t.closest('.ae-rv__size');
  if (size) {
    const card = size.closest('.ae-rv__card');
    const label = size.textContent;
    size.textContent = '…'; size.disabled = true;
    try { await aeAddToCart(size.dataset.id, card); openCartDrawer(); } catch (err) {}
    size.textContent = label; size.disabled = false;
    card.querySelector('.ae-rv__sizes').hidden = true; card.classList.remove('is-open');
  }
});

/* ---------- boot ---------- */
/* EasySize puts its button inside the variant picker, which is replaced on every variant change.
   Move it into the fit-quiz block (next to "Size guide") so it sits on one line and survives. */
function placeFitQuiz() {
  document.querySelectorAll('variant-picker #easysize_button').forEach((btn) => {
    const host = btn.closest('.product-details, #quick-add-modal-content, product-component') || document;
    const slot = host.querySelector('.fit-quiz-wrapper .easysize-container');
    if (!slot) return;
    slot.querySelectorAll('#easysize_button').forEach((old) => old !== btn && old.remove());
    slot.appendChild(btn);
  });
}

// AE: "Add this and your delivery is free" (PDP, only when the bag holds exactly 1 piece)
async function aeFreeDeliveryNudge() {
  const details = document.querySelector('.product-details .group-block-content');
  if (!details) return;
  let el = details.querySelector('.ae-fd-nudge');
  if (!el) {
    const titleGroup = [...details.children].find((c) => c.querySelector && c.querySelector('h1'));
    if (!titleGroup) return;
    el = document.createElement('p');
    el.className = 'ae-fd-nudge';
    el.hidden = true;
    el.textContent = 'Add this and your delivery is free.';
    titleGroup.insertAdjacentElement('afterend', el);
  }
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    el.hidden = cart.item_count !== 1;
  } catch (e) { el.hidden = true; }
}
document.addEventListener('cart:update', () => setTimeout(aeFreeDeliveryNudge, 300));
// Horizon has no cart event in this version: watch for cart requests instead (read-only, doesn't touch fetch)
try {
  new PerformanceObserver((list) => {
    if (list.getEntries().some((e) => /\/cart\/(add|change|update|clear)/.test(e.name))) setTimeout(aeFreeDeliveryNudge, 400);
  }).observe({ type: 'resource', buffered: false });
} catch (e) {}

// AE: multi-colour listings only show the selected colour's images
let aeProdPromise = null;
function aeSelectedColour() {
  for (const fs of document.querySelectorAll('.product-details variant-picker fieldset')) {
    const legend = (fs.querySelector('legend')?.textContent || '').toLowerCase();
    if (legend.includes('colo')) {
      const inp = fs.querySelector('input:checked');
      if (inp) return inp.value;
    }
  }
  return null;
}
async function aeColourGallery() {
  const gal = document.querySelector('.product-information media-gallery, media-gallery');
  if (!gal || !location.pathname.includes('/products/')) return;
  const handle = location.pathname.split('/products/')[1].split(/[/?#]/)[0];
  aeProdPromise = aeProdPromise || fetch('/products/' + handle + '.js').then((r) => r.json()).catch(() => null);
  const p = await aeProdPromise;
  if (!p) return;
  const ci = p.options.findIndex((o) => /colou?r/i.test(o.name || o));
  if (ci < 0 || p.options[ci].values.length < 2) return;
  const ids = p.media.map((m) => String(m.id));
  const starts = {};
  p.variants.forEach((v) => {
    const fm = v.featured_media && String(v.featured_media.id);
    const i = fm ? ids.indexOf(fm) : -1;
    const c = v.options[ci];
    if (i >= 0 && (starts[c] === undefined || i < starts[c])) starts[c] = i;
  });
  if (Object.keys(starts).length < p.options[ci].values.length) return;
  const colour = aeSelectedColour() || p.variants[0].options[ci];
  if (gal.dataset.aeColour === colour && gal.querySelector('[data-ae-hidden]')) return;
  const order = Object.values(starts).sort((a, b) => a - b);
  const a = starts[colour];
  if (a === undefined) return;
  const b = order.find((x) => x > a) ?? ids.length;
  const keep = new Set(ids.slice(a, b));
  let shown = 0;
  gal.querySelectorAll('[data-media-id]').forEach((m) => {
    const slide = m.closest('slideshow-slide, li, .product-media-container') || m;
    const on = keep.has(m.dataset.mediaId);
    if (on) { slide.style.display = ''; slide.removeAttribute('data-ae-hidden'); shown++; }
    else { slide.style.display = 'none'; slide.setAttribute('data-ae-hidden', ''); }
  });
  gal.dataset.aeColour = colour;
  gal.classList.toggle('ae-gallery-filtered', shown > 0);
  const counter = gal.querySelector('.slideshow-controls__counter');
  if (counter && counter.lastChild) counter.lastChild.textContent = String(shown);
  const first = gal.querySelector('slideshow-slide:not([data-ae-hidden])');
  const scroller = first && first.parentElement;
  if (scroller && scroller.scrollLeft !== undefined) scroller.scrollLeft = 0;
}
document.addEventListener('change', (e) => {
  if (e.target.closest && e.target.closest('.product-details variant-picker')) setTimeout(aeColourGallery, 350);
});

// AE: Judge.me testimonials header shows "4.75 ★ (2,518)"; match the rest of the site ("4.8 from 2,518 reviews")
function aeRoundJdgmRating() {
  document.querySelectorAll('.jdgm-rating-text:not([data-ae])').forEach((el) => {
    const m = (el.textContent || '').match(/([\d.]+)\s*★?\s*\(([\d,.\s]+)\)/);
    if (!m) return;
    const n = parseInt(m[2].replace(/[^\d]/g, ''), 10);
    const shown = (window.aeRating && String(window.aeRating).trim()) || parseFloat(m[1]).toFixed(2);
    el.textContent = shown + ' from ' + (isNaN(n) ? m[2].trim() : n.toLocaleString('en-US')) + ' reviews';
    el.setAttribute('data-ae', '1');
  });
}
// AE: mirror Horizon's live product count (hidden on mobile) into the quick-filter bar
function aeSyncCount() {
  const dst = document.querySelector('.ae-cbar__count');
  const src = document.querySelector('.products-count-wrapper [role=status]');
  if (!dst || !src) return;
  const n = parseInt((src.textContent || '').replace(/[^\d]/g, ''), 10);
  if (isNaN(n)) return;
  const txt = n.toLocaleString('en-US') + (n === 1 ? ' piece' : ' pieces');
  if (dst.textContent !== txt) dst.textContent = txt;
}
// AE: the announcement bar can't vary by currency, so non-USD shoppers get the percentage wording
function aeLocalOffer() {
  const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  if (cur === 'USD') return;
  document.querySelectorAll('header-announcements *, .announcement-bar *, [class*="announcement"] *').forEach((el) => {
    if (el.children.length || el.dataset.aeOffer) return;
    if (/3 tees for \$150/.test(el.textContent)) {
      el.textContent = el.textContent.replace(/3 tees for \$150/, '15% off any 3 tees');
      el.dataset.aeOffer = '1';
    }
  });
}
// AE: Shop Pay instalments message sits under the buy buttons rather than under the price
function aeMoveInstallments() {
  const details = document.querySelector('.product-details');
  if (!details) return;
  const buy = details.querySelector('.buy-buttons-block');
  if (!buy) return;
  details.querySelectorAll('.installments').forEach((inst) => {
    if (inst.dataset.aeMoved) return;
    details.querySelectorAll('.installments[data-ae-moved]').forEach((old) => old.remove());
    inst.dataset.aeMoved = '1';
    inst.classList.add('ae-installments');
    buy.insertAdjacentElement('afterend', inst);
  });
}
function scan(rootNode = document) {
  aeMoveInstallments();
  aeLocalOffer();
  aeSyncCount();
  placeFitQuiz();
  aeColourGallery();
  aeRoundJdgmRating();
  rootNode.querySelectorAll('variant-picker').forEach(initPicker);
  document.querySelectorAll('quick-add-dialog dialog[open]').forEach(enhanceQuickAdd);
}
function boot() {
  scan();
  aeFreeDeliveryNudge();
  document.querySelectorAll('.ae-rv[data-current]').forEach((el) => renderRecentlyViewed(el));
  if (location.hash === '#ae-fit') {
    // Arrived from "Find your size" in quick add: open the fit quiz straight away
    history.replaceState(null, '', location.pathname + location.search);
    const started = Date.now();
    const tryOpen = () => {
      const btn = document.querySelector('#easysize_button');
      if (btn) { btn.scrollIntoView({ block: 'center' }); btn.click(); return; }
      if (Date.now() - started < 10000) setTimeout(tryOpen, 300);
      else document.querySelector('.fit-quiz-wrapper')?.scrollIntoView({ block: 'center' });
    };
    tryOpen();
  }
  let queued = false;
  new MutationObserver(() => {
    if (queued) return; queued = true;
    setTimeout(() => { queued = false; scan(); }, 40);
  }).observe(document.body, { childList: true, subtree: true });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

/* ---------- Set builder (top + sweatpants) ---------- */
async function aeAddLinesToCart(lines, sourceEl) {
  const ev = await loadEvents();
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId).filter(Boolean);
  let deferred = null;
  if (ev && ev.CartLinesUpdateEvent) {
    deferred = ev.CartLinesUpdateEvent.createPromise();
    (sourceEl || document).dispatchEvent(new ev.CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: 1 })), promise: deferred.promise,
    }));
  }
  const body = { items: lines.map((l) => ({ id: Number(l.id), quantity: 1 })) };
  if (sectionIds.length) body.sections = sectionIds.join(',');
  const res = await fetch('/cart/add.js', {
    method: 'POST', body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
  }).then((r) => r.json());
  const cart = await fetch('/cart.js').then((r) => r.json());
  if (deferred) {
    deferred.resolve({
      cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'product-form-component', sourceId: 'ae-set', itemCount: lines.length,
                sections: res.sections, didError: Boolean(res.status) },
    });
  }
  if (res.status) throw new Error(res.description || res.message || 'Could not add to cart');
  return cart;
}

function aeInitSet(el) {
  if (el.dataset.aeInit) return; el.dataset.aeInit = '1';
  let data = [];
  try { data = JSON.parse(el.querySelector('[data-set-json]').textContent); } catch (e) { return; }
  const pct = Number(el.dataset.pct || 20) / 100;
  const mode = el.dataset.mode; // 'top' = this page is the top; 'bottom' = this page is the sweatpants
  const selfSel = el.querySelector('[data-select="self"]');
  const prodSel = el.querySelector('[data-select="other-product"]');
  const otherSel = el.querySelector('[data-select="other"]');
  const imgBox = el.querySelector('[data-other-img]');
  const totalEl = el.querySelector('[data-total]');
  const btn = el.querySelector('[data-add]');
  const norm = (c) => String(c || '').toLowerCase().replace(/grey/g, 'gray').trim();
  const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

  // One entry per product + colour, so a multi-colour product can be matched by colour.
  const entries = [];
  data.forEach((p) => {
    const colours = [];
    p.variants.forEach((v) => { if (!colours.includes(v.color)) colours.push(v.color); });
    colours.forEach((col) => {
      const vs = p.variants.filter((v) => v.color === col);
      let label = p.title;
      if (col && colours.length > 1 && !norm(p.title).endsWith(norm(col))) label += ' - ' + col;
      const vimg = vs.find((v) => v.img);
      entries.push({ key: p.id + '|' + col, label, color: norm(col) || norm(p.title.split(' - ').pop()), match: !!p.match, img: (colours.length > 1 && vimg) ? vimg.img : p.img, variants: vs });
    });
  });
  if (!entries.length) return;

  // The main product's variant picker on this page (not quick-add pickers).
  const picker = document.querySelector('variant-picker[data-template-product-match="true"]');
  const pickedValue = (name) => {
    const root = document.querySelector('variant-picker[data-template-product-match="true"]');
    const i = root && root.querySelector(`input[data-option-name="${name}"]:checked, input[data-option-name="${name.toLowerCase()}"]:checked`);
    return i ? i.value : '';
  };
  const selfColour = () => norm(pickedValue('Color') || (selfSel.options[1] && selfSel.options[1].dataset.color) || '');
  let userPickedProduct = false;
  let userPickedOther = false;

  function bestEntry() {
    const col = selfColour();
    return entries.find((e) => e.match && e.color === col) || entries.find((e) => e.match) ||
      entries.find((e) => e.color === col) || entries[0];
  }
  function current() { return entries.find((e) => e.key === prodSel.value) || entries[0]; }
  function fillProducts() {
    prodSel.innerHTML = entries.map((e) => `<option value="${esc(e.key)}">${esc(e.label)}</option>`).join('');
    prodSel.value = bestEntry().key;
  }
  function fillOther(size) {
    const e = current();
    const keep = size || (otherSel.selectedOptions[0] && otherSel.selectedOptions[0].dataset.size) || '';
    otherSel.innerHTML = '<option value="">Select size</option>' +
      e.variants.map((v) => `<option value="${v.id}" data-price="${v.price}" data-size="${esc(v.size)}">${esc(v.size || v.title)}</option>`).join('');
    if (keep) setSize(otherSel, keep);
    imgBox.innerHTML = e.img ? `<img src="${e.img}" alt="" width="64" height="80">` : '';
    render();
  }
  function setSize(sel, size, colour) {
    const o = Array.from(sel.options).find((x) => x.value && x.dataset.size === size && (!colour || !x.dataset.color || norm(x.dataset.color) === colour));
    if (o) { sel.value = o.value; sel.classList.remove('ae-need'); return true; }
    return false;
  }
  // Sync from the size (and colour) chosen in the product form above.
  function syncFromPicker() {
    const size = pickedValue('Size');
    if (!userPickedProduct) { const b = bestEntry(); if (b.key !== prodSel.value) { prodSel.value = b.key; fillOther(); } }
    if (!size) return;
    setSize(selfSel, size, selfColour());
    if (!userPickedOther) setSize(otherSel, size);
    render();
  }
  function priceOf(sel, fallback) {
    const o = sel.selectedOptions[0];
    return o && o.dataset.price ? Number(o.dataset.price) : fallback;
  }
  function render() {
    const e = current();
    const selfFallback = Number(selfSel.options[1] ? selfSel.options[1].dataset.price : 0);
    const selfP = priceOf(selfSel, selfFallback);
    const otherP = priceOf(otherSel, e.variants.length ? e.variants[0].price : 0);
    const pants = mode === 'top' ? otherP : selfP;
    const full = selfP + otherP;
    const setP = Math.round(full - pants * pct);
    totalEl.innerHTML = `Set price ${money(setP)}<s>${money(full)}</s>`;
  }
  function need(sel) { sel.classList.remove('ae-need'); void sel.offsetWidth; sel.classList.add('ae-need'); sel.focus(); }
  selfSel.addEventListener('change', () => {
    selfSel.classList.remove('ae-need');
    const o = selfSel.selectedOptions[0];
    if (o && o.dataset.size && !userPickedOther) setSize(otherSel, o.dataset.size);
    render();
  });
  otherSel.addEventListener('change', () => { otherSel.classList.remove('ae-need'); userPickedOther = !!otherSel.value; render(); });
  prodSel.addEventListener('change', () => { userPickedProduct = true; fillOther(); });
  document.addEventListener('change', (ev) => {
    const t = ev.target;
    if (!(t instanceof HTMLInputElement) || !t.dataset.optionName) return;
    const vp = t.closest('variant-picker');
    if (!vp || vp.dataset.templateProductMatch !== 'true') return;
    setTimeout(syncFromPicker, 0);
  });
  btn.addEventListener('click', async () => {
    if (!selfSel.value) return need(selfSel);
    if (!otherSel.value) return need(otherSel);
    btn.disabled = true; const label = btn.textContent; btn.textContent = 'Adding…';
    try { await aeAddLinesToCart([{ id: selfSel.value }, { id: otherSel.value }], el); btn.textContent = 'Added'; openCartDrawer(); }
    catch (err) { btn.textContent = 'Try again'; }
    setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 2200);
  });
  fillProducts();
  fillOther();
  // Only prefill sizes on load if a size is already chosen via the URL (?variant=); otherwise wait for the customer to pick one above.
  if (picker && /[?&]variant=/.test(location.search)) syncFromPicker();
}
function aeScanSets() { document.querySelectorAll('[data-ae-set]').forEach(aeInitSet); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', aeScanSets); else aeScanSets();
document.addEventListener('shopify:section:load', aeScanSets);

/* AE: card image swipe hand-off. When a product card's images are already on the last image and the
   customer swipes on in the same direction (or on the first image and swipes back), move the row instead. */
(function () {
  let start = null;
  const rowOf = (el) => {
    let n = el.parentElement;
    while (n && n !== document.body) {
      const cs = getComputedStyle(n);
      if ((cs.overflowX === 'auto' || cs.overflowX === 'scroll') && n.scrollWidth > n.clientWidth + 2) return n;
      n = n.parentElement;
    }
    return null;
  };
  document.addEventListener('touchstart', (e) => {
    const slides = e.target.closest && e.target.closest('product-card slideshow-slides, .product-card slideshow-slides');
    if (!slides || e.touches.length !== 1) { start = null; return; }
    const t = e.touches[0];
    start = {
      slides, x: t.clientX, y: t.clientY,
      atEnd: slides.scrollLeft + slides.clientWidth >= slides.scrollWidth - 2,
      atStart: slides.scrollLeft <= 2,
    };
  }, { passive: true });
  document.addEventListener('touchend', (e) => {
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x, dy = t.clientY - start.y;
    const s = start; start = null;
    if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy)) return;
    const forward = dx < 0;
    if ((forward && !s.atEnd) || (!forward && !s.atStart)) return;
    const row = rowOf(s.slides);
    if (!row) return;
    const step = s.slides.clientWidth + 6;
    row.scrollBy({ left: forward ? step : -step, behavior: 'smooth' });
  }, { passive: true });
})();

/* AE accessibility: carousel rows (Shop by product, product rows) are scrolling lists, so every item
   stays readable to screen readers and AI agents. Horizon marks off-screen slides aria-hidden="true"
   while their links stay tabbable, which fails the accessibility and agentic-browsing checks. */
(function () {
  const SEL = 'slideshow-slide.resource-list__slide';
  const fix = (el) => { if (el.getAttribute('aria-hidden') === 'true') el.setAttribute('aria-hidden', 'false'); };
  const scan = (root) => root.querySelectorAll && root.querySelectorAll(SEL + '[aria-hidden="true"]').forEach(fix);
  const start = () => {
    scan(document);
    new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === 'attributes') { if (m.target.matches && m.target.matches(SEL)) fix(m.target); }
        else m.addedNodes.forEach((n) => { if (n.nodeType === 1) { if (n.matches(SEL)) fix(n); scan(n); } });
      }
    }).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-hidden'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();

/* AE speed: fetch "Load on scroll" rows (product-list sections rendered as .ae-deferred-row) about two screens ahead. */
(function () {
  const rows = [...document.querySelectorAll('[data-ae-defer]')];
  if (!rows.length) return;
  const load = (host, id, tries) => {
    const url = new URL(location.pathname, location.origin);
    url.searchParams.set('section_id', id);
    fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((html) => {
        const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById('shopify-section-' + id);
        if (!fresh || fresh.querySelector('[data-ae-defer]')) throw new Error('no content');
        host.replaceChildren(document.createRange().createContextualFragment(fresh.innerHTML));
      })
      .catch(() => { if (tries < 2) setTimeout(() => load(host, id, tries + 1), 1500); });
  };
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      load(e.target, e.target.dataset.aeHost, 0);
    });
  }, { rootMargin: '1800px 0px' });
  rows.forEach((el) => {
    const host = el.closest('.shopify-section');
    if (!host) return;
    host.dataset.aeHost = el.dataset.aeDefer;
    io.observe(host);
  });
})();
